import type { AxiosInstance } from 'axios';
import {
  createRequestInterceptor,
  requestInterceptorErrorHandler,
  type RequestInterceptorDependencies,
} from './request';
import {
  responseInterceptorSuccessHandler,
  createResponseInterceptorErrorHandler,
  type ResponseInterceptorDependencies,
} from './response';

/**
 * Dependencies for setting up interceptors.
 * Combines dependencies needed for both request and response interceptors.
 * Note: clientType and appVersion are inherited from RequestInterceptorDependencies
 */
export interface SetupInterceptorsDependencies
  extends RequestInterceptorDependencies,
    ResponseInterceptorDependencies {
  /** Axios instance to attach interceptors to */
  authenticatedInstance: AxiosInstance;

  /** Base URL for API requests (passed through to token refresh) */
  baseURL: string;

  /** Function to refresh access token (passed through to response interceptor) */
  refreshAccessToken: () => Promise<string>;
}

/**
 * Setup request and response interceptors on an axios instance.
 *
 * Configures:
 * 1. Request interceptor - adds Authorization header, proactive token refresh
 * 2. Response interceptor - handles 401/403/network errors with retry logic
 *
 * @param deps Dependencies (instances, auth store, state, config, handlers)
 *
 * @example
 * ```typescript
 * setupInterceptors({
 *   authenticatedInstance,
 *   authStore,
 *   state,
 *   config,
 *   handleTokenRefresh,
 *   onAuthFailure,
 *   onNetworkError,
 *   retryQueuedRequests,
 * });
 * ```
 */
export function setupInterceptors(deps: SetupInterceptorsDependencies): void {
  const { authenticatedInstance } = deps;

  // Request interceptor - add token and check for proactive refresh
  authenticatedInstance.interceptors.request.use(
    createRequestInterceptor(deps),
    requestInterceptorErrorHandler
  );

  // Response interceptor - handle 401/403 and token refresh
  authenticatedInstance.interceptors.response.use(
    responseInterceptorSuccessHandler,
    createResponseInterceptorErrorHandler(deps)
  );
}
